import { useEffect, useState, type ReactNode } from "react";
import Header from "./Header";
import getStyles from "./styles";
import AdminPanel from "../AdminPanel";
import TotalsTable from "../TotalsTable";
import ResetCountModal from "./ResetCountModal";
import CallCenterPanel from "../CallCenterPanel";
import ChangePasswordModal from "./ChangePasswordModal";
import { useAppDispatch, useAppSelector } from "../../redux";
import { selectIsAuthentication, selectSuccess } from "../../AppSlice";
import {
  selectRole,
  selectImages,
  getUsersAction,
  logoutAdminAction,
  getTotalUsersAction,
  selectPaneemTotalUsers,
  selectPortalTotalUsers,
  selectUsername,
  selectPermissions,
} from "./DashboardSlice";
import { AppBar, Box, Grid, Tab, Tabs, Typography } from "@mui/material";
import { NewUser } from "../AdminPanel/NewUser";
import { Queue } from "../Queue/Queue";
import { cleanUnderReviewAction } from "../Queue/QueueSlice";
import type { Permissions } from "../../types";

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ peddingTop: 0 }}>{children}</Box>}
    </div>
  );
}

export default function _Dashboard() {
  const styles = getStyles();

  const dispatch = useAppDispatch();

  const role = useAppSelector(selectRole);
  const images = useAppSelector(selectImages);
  const success = useAppSelector(selectSuccess);
  const username = useAppSelector(selectUsername);
  const isAuthentication = useAppSelector(selectIsAuthentication);
  const paneemTotalUsers = useAppSelector(selectPaneemTotalUsers);
  const portalTotalUsers = useAppSelector(selectPortalTotalUsers);
  const permissions = useAppSelector(selectPermissions);
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [isResetOpen, setIsResetOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [isNewUserOpen, setIsNewUserOpen] = useState<boolean>(false);

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;

    s.src = `https://arn.upraise.io/rest/arn/latest/widget/widget-init?widgetId=3e3f1408-84fc-43e3-957d-f8f1b4d408c1&clientKey=0542706e-2702-31e0-83a8-a17f32f7d306&userId=${username}`;

    s.onload = () => {
      try {
        new window.arnWidget();
      } catch (error) {
        console.error("Failed to initialize the widget:", error);
      }
    };

    const head = document.querySelector("head");

    head?.appendChild(s);

    return () => {
      head?.removeChild(s);
    };
  }, []);

  const handleSyncData = async (): Promise<any> => {
    setPage(0);

    await dispatch(getUsersAction("0"));
  };

  const handleOpenPassword = () => setIsPasswordOpen(true);

  const handleOpenAttempts = () => setIsResetOpen(true);

  const handleCloseModals = () => {
    setIsPasswordOpen(false);
    setIsResetOpen(false);
  };

  const handleLogout = async () => await dispatch(logoutAdminAction());

  const getTab = (permissions: Permissions | null): number => {
    if (!permissions) {
      return 0;
    }
    if (permissions.entrie) {
      return 0;
    } else if (permissions.queue) {
      return 2;
    } else {
      return 3;
    }
  };

  const [value, setValue] = useState(getTab(permissions));

  const handleChange = async (event: React.SyntheticEvent) => {
    // @ts-ignore
    setValue(event.target.tabIndex);
    // @ts-ignore
    const value = event.target.tabIndex;
    if (value === 1) {
      if (paneemTotalUsers.length && portalTotalUsers.length) {
        return;
      }
      await dispatch(getTotalUsersAction());
    }
    if (value === 2) await dispatch(cleanUnderReviewAction());
  };

  return (
    <Grid container direction={"row"}>
      <Grid item xs={12} xl={12} sx={{ width: "100%" }}>
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              justifyContent: "space-between",
            }}
          >
            <AppBar position="static" color="primary" style={{ padding: 10, alignItems: "space-evenly" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="basic tabs example"
                sx={{ alignItems: "stretch" }}
              >
                <Tab
                  label="Entries"
                  tabIndex={0}
                  sx={{
                    id: `simple-tab-${0}`,
                    "aria-controls": `simple-tabpanel-${0}`,
                    display: `${permissions?.entrie ? "inline-flex" : "none"}`,
                  }}
                />
                <Tab
                  label="Totals"
                  tabIndex={1}
                  sx={{
                    id: `simple-tab-${1}`,
                    "aria-controls": `simple-tabpanel-${1}`,
                    display: `${permissions?.total ? "inline-flex" : "none"}`,
                  }}
                />
                <Tab
                  label="Queue"
                  tabIndex={2}
                  sx={{
                    id: `simple-tab-${2}`,
                    "aria-controls": `simple-tabpanel-${2}`,
                    display: `${permissions?.queue ? "inline-flex" : "none"}`,
                  }}
                />
                <Tab
                  tabIndex={3}
                  label="Paneem Status"
                  sx={{
                    id: `simple-tab-${3}`,
                    "aria-controls": `simple-tabpanel-${3}`,
                    display: `${permissions?.paneemStatus ? "inline-flex" : "none"}`,
                  }}
                />
                <Header
                  role={role}
                  handleSyncData={handleSyncData}
                  handleOpenAttempts={handleOpenAttempts}
                  handleOpenPassword={handleOpenPassword}
                  handleLogout={handleLogout}
                  hendleOpenNewUser={() => setIsNewUserOpen(true)}
                />
              </Tabs>
            </AppBar>
          </Box>
          <Typography variant="h3" textAlign={"left"}>
            Welcome {username}
          </Typography>
          <CustomTabPanel value={value} index={0}>
            <AdminPanel images={images} isAuthentication={isAuthentication} page={page} setPage={setPage} />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <TotalsTable users={paneemTotalUsers} tableTitle="PANEEM" />
            <TotalsTable users={portalTotalUsers} tableTitle="Survivor's Portal" />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <Queue />
          </CustomTabPanel>
          <CustomTabPanel value={value} index={3}>
            <CallCenterPanel />
          </CustomTabPanel>
        </Box>
      </Grid>

      {permissions?.resetAttempt ? (
        <ResetCountModal isOpen={isResetOpen} success={success} styles={styles} handleClose={handleCloseModals} />
      ) : null}
      <ChangePasswordModal isOpen={isPasswordOpen} success={success} styles={styles} handleClose={handleCloseModals} />

      {permissions?.newUser ? (
        <NewUser isOpen={isNewUserOpen} success={success} handleClose={() => setIsNewUserOpen(false)} />
      ) : null}
    </Grid>
  );
}
